import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { getEloTier } from '../../utils/eloUtils';

interface RankingUser {
  id: string;
  username: string;
  elo_rating: number;
  avatar_url?: string;
}

interface RankingListProps {
  users: RankingUser[];
  currentUserId?: string;
  preview?: boolean;
  context?: 'challenges' | 'home';
  startRank?: number;
}

const RankingList: React.FC<RankingListProps> = ({ 
  users, 
  currentUserId, 
  preview = true,
  context = 'home',
  startRank = 1
}) => {
  const { t } = useTranslation();
  // Ensure users is always an array
  const safeUsers = Array.isArray(users) ? users : [];
  const displayUsers = preview ? safeUsers.slice(0, 5) : safeUsers;

  const translations = {
    title: context === 'challenges' 
      ? t('challenges.ranking.title') 
      : t('ranking.ranking'),
    description: context === 'challenges'
      ? t('challenges.ranking.description')
      : t('ranking.description'),
    viewFull: context === 'challenges'
      ? t('challenges.ranking.viewFull')
      : t('ranking.viewFull'),
    elo: context === 'challenges'
      ? t('challenges.ranking.elo')
      : t('ranking.elo'),
    moreUsers: context === 'challenges'
      ? t('challenges.ranking.moreUsers', { count: Math.max(0, safeUsers.length - 5) })
      : t('ranking.moreUsers', { count: Math.max(0, safeUsers.length - 5) })
  };

  // Animation variants for list items
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
  };

  return (
    <div className="bg-base-100/90 backdrop-blur-md rounded-box p-4 border border-primary/20 shadow-lg overflow-hidden">
      {preview && (
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-bold text-primary">{translations.title}</h3>
            {context === 'challenges' && (
              <p className="text-sm text-base-content/80">{translations.description}</p>
            )}
          </div>
          {preview && (
            <Link 
              to="/ranking" 
              className="btn btn-ghost btn-sm normal-case gap-2 text-primary hover:bg-primary/10"
            >
              {translations.viewFull}
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          )}
        </div>
      )}

      <motion.div 
        className="flex flex-col gap-2"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {displayUsers.map((user, index) => {
          const tier = getEloTier(user.elo_rating);
          const position = startRank + index;
          const medal = position <= 3 ? ['🥇', '🥈', '🥉'][position - 1] : null;
          const isCurrentUser = user.id === currentUserId;
          
          // Special styling for top 3
          const isTop3 = position <= 3;
          const topStyles = isTop3 ? {
            1: 'bg-gradient-to-r from-primary/30 to-primary/5 border-primary/30 shadow-md',
            2: 'bg-gradient-to-r from-primary/20 to-primary/5 border-primary/20',
            3: 'bg-gradient-to-r from-primary/10 to-primary/5 border-primary/10'
          } : {};

          return (
            <motion.div 
              key={user.id}
              variants={item}
              className={`grid grid-cols-[auto,1fr,auto] items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border 
                ${isCurrentUser 
                  ? 'bg-primary/20 border-primary/40 shadow-md'
                  : isTop3 
                    ? topStyles[position as keyof typeof topStyles] 
                    : 'bg-base-200/80 hover:bg-base-200 border-transparent'
                } 
                transition-all hover:shadow-sm`}
            >
              {/* Position */}
              <div className={`w-6 sm:w-8 text-center font-bold flex-shrink-0 ${isTop3 ? 'text-xl sm:text-2xl' : 'opacity-70'}`}>
                {medal || (position > 99 ? `${position}` : `#${position}`)}
              </div>

              {/* User info */}
              <div className="flex items-center gap-1 sm:gap-2 min-w-0 overflow-hidden">
                <div className="avatar flex-shrink-0">
                  <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full ring ${isTop3 || isCurrentUser 
                    ? 'ring-primary/80 ring-offset-1 sm:ring-offset-2' 
                    : 'ring-primary/40 ring-offset-1'} ring-offset-base-100`}>
                    {user.avatar_url ? (
                      <img 
                        src={user.avatar_url} 
                        alt={user.username || 'User'}
                        className="object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = `https://api.dicebear.com/7.x/bottts/svg?seed=${user.username || 'user'}`;
                        }}
                      />
                    ) : (
                      <img 
                        src={`https://api.dicebear.com/7.x/bottts/svg?seed=${user.username || 'user'}`}
                        alt={user.username || 'User'}
                      />
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 sm:gap-2 min-w-0 overflow-hidden">
                  <div className="w-4 h-4 sm:w-6 sm:h-6 flex-shrink-0">
                    <svg viewBox="0 0 24 24" fill={tier.color}>
                      <path d={tier.icon} />
                    </svg>
                  </div>
                  <span className="font-medium text-sm sm:text-base truncate max-w-[120px] sm:max-w-none">
                    {user.username || 'User'}
                  </span>
                </div>
              </div>

              {/* ELO */}
              <div className="flex items-center gap-1 sm:gap-2 justify-end flex-shrink-0 tabular-nums">
                <span className="text-xs sm:text-sm opacity-70 hidden sm:inline whitespace-nowrap">{translations.elo}</span>
                <span className={`font-bold text-sm sm:text-base ${isTop3 ? 'text-primary' : ''} whitespace-nowrap`}>{user.elo_rating}</span>
              </div>
            </motion.div>
          );
        })}

        {preview && safeUsers.length > 5 && (
          <motion.div 
            variants={item}
            className="text-center text-base-content/60 text-sm mt-2 hover:text-primary transition-colors"
          >
            <Link to="/ranking">{translations.moreUsers}</Link>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default RankingList;
